import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Utensils, ShoppingBag, ShieldCheck, Wallet, CreditCard, ArrowRight, Loader2 } from "lucide-react";
import { QrSheet } from "@/pages/public/qr/QrSheet";
import { publicApi, money } from "./orderApi";
import { deviceId } from "./useCart";
import { PayOnlineSheet } from "./PayOnlineSheet";
import { OtpVerify } from "./OtpVerify";
import { localPhone, displayPhone } from "./useSession";
import { useOrderLocation } from "./useOrderLocation";
import { LocationStatus } from "./LocationStatus";

const errText = (e, fb) => { const d = e?.response?.data?.detail; return (d && (d.message || (typeof d === "string" ? d : null))) || fb; };
const newReqId = () => (window.crypto?.randomUUID?.() || `${Date.now()}${Math.random().toString(16).slice(2)}`).replace(/-/g, "");

/**
 * Checkout: order type → details → verification (skipped for a valid 30-day session) → payment → review → explicit Place order.
 * Pricing is server-authoritative; OTP never places an order; one client_request_id per attempt prevents duplicates.
 */
export const Checkout = ({ open, onClose, slug, branch, otpWidget, cart, color, brand, session, orderType: outerType, onOrderType }) => {
  const nav = useNavigate();
  const orderType = outerType || (branch?.accepts_dine_in ? "dine_in" : "takeaway");
  const setOrderType = (t) => onOrderType && onOrderType(t);
  const [payMethod, setPayMethod] = useState(() => (branch?.payments?.pay_online ? "razorpay" : branch?.payments?.pay_at_outlet === false ? null : "pay_at_outlet"));
  const [paying, setPaying] = useState(null);
  const [name, setName] = useState(session.name || "");
  const [phone, setPhone] = useState(() => (session.valid ? session.phone.replace(/^\+91/, "") : localPhone()));
  const [verification, setVerification] = useState(null); // fresh OTP proof for this phone (when no session)
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState("");
  const reqId = useRef(newReqId());
  // Order-time GPS capture for THIS checkout session (hook runs before the `if (!open) return null` below).
  const geo = useOrderLocation(open);

  useEffect(() => { if (session.valid) { setPhone(session.phone.replace(/^\+91/, "")); if (session.name && !name) setName(session.name); } }, [session.valid, session.phone]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;

  const phoneOk = /^[6-9]\d{9}$/.test(phone);
  const verified = session.valid || !!verification;
  const pm = branch.payments || { pay_online: false, pay_at_outlet: true };
  const total = cart.priced?.total;

  const onVerified = async (tok) => {
    setVerification(tok);
    try {
      const a = await publicApi.post(`/${slug}/account/login`, { branch_id: branch.id, verification_token: tok, phone, device_id: deviceId(), customer_name: name.trim() || null });
      session.save(a.data.account_token, a.data.phone, name.trim());
    } catch { /* order can still be placed with the fresh verification token */ }
  };
  const changeNumber = async () => { await session.logout(); setVerification(null); setPhone(""); };

  const place = async () => {
    if (placing) return;
    setPlacing(true); setError("");
    try {
      session.rememberName(name.trim());
      localStorage.setItem("sc_online_phone", phone);
      // Never waits for GPS: synchronous snapshot of the best state right now (fresh / stale with original
      // capture time / pending / denied / timeout / unavailable). Outdated captures refresh in the background.
      const location = geo.snapshot();
      const r = await publicApi.post(`/${slug}/branches/${branch.id}/orders`, {
        location,
        lines: cart.lines.map(({ item_id, variant_name, addon_ids, qty, note }) => ({ item_id, variant_name, addon_ids, qty, note })),
        coupon_code: cart.coupon || null, client_request_id: reqId.current,
        verification_token: verification || undefined, session_token: session.valid ? session.token : undefined,
        customer_phone: phone, device_id: deviceId(), customer_name: name.trim(), order_type: orderType, payment_method: payMethod,
      });
      cart.clear();
      const track = `/order/${slug}/track/${r.data.order.id}?t=${encodeURIComponent(r.data.tracking_token)}`;
      if (payMethod === "razorpay" && r.data.payment) setPaying({ requestId: r.data.order.id, t: r.data.tracking_token, intent: r.data.payment, track });
      else nav(track);
    } catch (e) {
      setError(errText(e, "Could not place the order. Please try again."));
      if (e?.response?.status === 401) { setVerification(null); if (session.valid) await session.logout(); }
      if (e?.response?.status !== 401 && !e?.response) { /* network: keep same client_request_id so a retry is idempotent */ } else reqId.current = newReqId();
    } finally { setPlacing(false); }
  };

  if (paying) {
    return <PayOnlineSheet slug={slug} requestId={paying.requestId} trackingToken={paying.t} intent={paying.intent} brand={brand} color={color}
      customer={{ name, phone }} onPaid={() => nav(paying.track)} onCancel={() => nav(paying.track)} />;
  }

  return (
    <QrSheet title="Checkout" id="checkout-sheet" onClose={onClose} busy={placing}>
      <div className="qr-sheet-scroll" style={{ paddingTop: 4 }}>
        <section className="os-step" data-testid="checkout-order-type">
          <h3><i>1</i>Order type</h3>
          <div className="os-seg">
            {branch.accepts_dine_in && <button type="button" className={orderType === "dine_in" ? "active" : ""} onClick={() => setOrderType("dine_in")} data-testid="order-type-dine-in"><Utensils size={16} />Dine-in</button>}
            {branch.accepts_takeaway && <button type="button" className={orderType === "takeaway" ? "active" : ""} onClick={() => setOrderType("takeaway")} data-testid="order-type-takeaway"><ShoppingBag size={16} />Takeaway</button>}
          </div>
          {orderType === "dine_in" && <p className="os-hint">No table booking needed — show your token at the counter and our team will seat you.</p>}
        </section>

        <section className="os-step" data-testid="checkout-customer">
          <h3><i>2</i>Your details</h3>
          {session.valid ? (
            <div className="os-verified" data-testid="session-verified"><ShieldCheck size={20} /><div style={{ flex: 1 }}>Mobile verified<small data-testid="session-phone">{displayPhone(session.phone)}</small></div>
              <button type="button" className="os-btn text" onClick={changeNumber} data-testid="session-change-number">Change</button></div>
          ) : (
            <div className="os-input-row" style={{ marginBottom: 8 }}>
              <span className="os-prefix">+91</span>
              <input className="os-input" value={phone} inputMode="numeric" autoComplete="tel-national" onChange={(e) => { setPhone(e.target.value.replace(/\D/g, "").slice(0, 10)); setVerification(null); }} placeholder="Mobile number" disabled={!!verification} data-testid="customer-phone" />
            </div>
          )}
          <input className="os-input" value={name} autoComplete="name" onChange={(e) => { const v = e.target.value.slice(0, 80); setName(v); session.rememberName(v); }} placeholder="Your name" data-testid="customer-name" style={{ marginTop: session.valid ? 10 : 0 }} />
        </section>

        {!session.valid && (
          <section className="os-step" data-testid="checkout-otp">
            <h3><i>3</i>Verify mobile</h3>
            {!phoneOk ? <p className="os-hint" style={{ margin: 0 }}>Enter a valid 10-digit mobile number to receive an SMS code.</p>
              : <OtpVerify slug={slug} branchId={branch.id} phone={phone} otpWidget={otpWidget} onVerified={onVerified} idPrefix="otp" />}
            {verification && <p className="os-hint" data-testid="otp-session-note">You’ll stay signed in on this browser for 30 days.</p>}
          </section>
        )}

        <section className="os-step" data-testid="checkout-payment">
          <h3><i>{session.valid ? 3 : 4}</i>Payment</h3>
          <div style={{ display: "grid", gap: 8 }}>
            {pm.pay_online && <button type="button" className={`os-paycard ${payMethod === "razorpay" ? "active" : ""}`} onClick={() => setPayMethod("razorpay")} data-testid="payment-pay-online"><CreditCard size={20} /><span>Pay Online<small>UPI · Cards · Netbanking</small></span></button>}
            {pm.pay_at_outlet && <button type="button" className={`os-paycard ${payMethod === "pay_at_outlet" ? "active" : ""}`} onClick={() => setPayMethod("pay_at_outlet")} data-testid="payment-pay-at-outlet"><Wallet size={20} /><span>Pay at outlet<small>Cash / UPI at the counter</small></span></button>}
            {!pm.pay_online && !pm.pay_at_outlet && <p className="os-err" data-testid="payment-none">No payment option is available at this outlet right now.</p>}
          </div>
        </section>

        <section className="os-step" data-testid="checkout-review">
          <h3><i>{session.valid ? 4 : 5}</i>Review</h3>
          <div className="os-bill" style={{ marginTop: 0 }}>
            {cart.lines.map((l, i) => <div key={i}><span>{l.qty} × {l.name}{l.variant_name ? ` (${l.variant_name})` : ""}</span></div>)}
            <div><span>Subtotal</span><b>{cart.priced ? money(cart.priced.subtotal) : "…"}</b></div>
            {cart.priced?.discount_total > 0 && <div className="disc"><span>Discount</span><b>− {money(cart.priced.discount_total)}</b></div>}
            {cart.priced?.tax_total > 0 && <div><span>Taxes</span><b>{money(cart.priced.tax_total)}</b></div>}
            <div className="total"><span>Total</span><b data-testid="checkout-total">{total != null ? money(total) : "…"}</b></div>
          </div>
        </section>
        {error && <p className="os-err" role="alert" data-testid="checkout-error">{error}</p>}
      </div>
      <div className="os-sheet-foot">
        <LocationStatus geo={geo} />
        <button type="button" onClick={place} disabled={!verified || placing || !cart.priced || cart.pricing || !payMethod || !name.trim() || !phoneOk} className="os-btn primary" data-testid="place-order">
          {placing ? <><Loader2 size={18} className="animate-spin" />Placing order…</> : <>{payMethod === "razorpay" ? "Place order & pay" : "Place order"} · {total != null ? money(total) : ""} <ArrowRight size={18} /></>}
        </button>
        {!verified && <p className="os-hint" style={{ textAlign: "center" }}>Verify your mobile to place the order.</p>}
      </div>
    </QrSheet>
  );
};
